import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@components/shadcn-ui/card";
import { Button } from "@components/shadcn-ui/button";
import { Edit, User, Loader2 } from "lucide-react";
import { addressService, AddressResponse } from "../service/addressService";

export interface CoApplicantData {
  addressId: string[] | string;
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
  };
  kycDetails?: {
    panNumber?: string;
  };
}

interface CoApplicantCardProps {
  coApplicant: CoApplicantData;
  onEdit: () => void;
  applicationId?: string;
}

export const CoApplicantCard: React.FC<CoApplicantCardProps> = ({
  coApplicant,
  onEdit,
  applicationId,
}) => {
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (!applicationId || !coApplicant.addressId) return;
      
      setIsLoadingAddresses(true);
      try {
        const addressIds = Array.isArray(coApplicant.addressId) 
          ? coApplicant.addressId 
          : [coApplicant.addressId];
        
        const fetchedAddresses: AddressResponse[] = [];
        
        for (const addressId of addressIds) {
          try {
            const address = await addressService.getAddressById(applicationId, addressId);
            if (address) {
              fetchedAddresses.push(address);
            }
          } catch (error) {
            console.error(`Error fetching address ${addressId}:`, error);
          }
        }
        
        setAddresses(fetchedAddresses);
      } catch (error) {
        console.error("Error fetching addresses:", error);
      } finally {
        setIsLoadingAddresses(false);
      }
    };

    fetchAddresses();
  }, [applicationId, coApplicant.addressId]);

  // Format address for display
  const formatAddress = (address: AddressResponse) => {
    const parts = [
      address.type,
      address.line1,
      address.line2,
      address.landmark,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);
    
    return parts.join(", ");
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="bg-gray-50 p-3 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-primary/10 p-2 rounded-full">
              <User className="h-5 w-5 text-primary" />
            </div>
            <h3 className="ml-2 font-medium">Co-Applicant</h3>
          </div>
          {onEdit && (
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
                <span className="sr-only">Edit</span>
              </Button>
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <p className="text-gray-500">Name</p>
              <p className="font-medium">
                {coApplicant.personalDetails.firstName} {coApplicant.personalDetails.lastName}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Relation</p>
              <p className="font-medium">{coApplicant.relationToApplicant}</p>
            </div>
            {coApplicant.personalDetails.phoneNumber && (
              <div>
                <p className="text-gray-500">Phone Number</p>
                <p className="font-medium">{coApplicant.personalDetails.phoneNumber}</p>
              </div>
            )}
            {coApplicant.personalDetails.email && (
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{coApplicant.personalDetails.email}</p>
              </div>
            )}
            {coApplicant.kycDetails?.panNumber && (
              <div>
                <p className="text-gray-500">PAN Number</p>
                <p className="font-medium">{coApplicant.kycDetails.panNumber}</p>
              </div>
            )}
            <div className="col-span-2">
              <p className="text-gray-500">Addresses</p>
              {isLoadingAddresses ? (
                <div className="flex items-center">
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  <span className="text-sm">Loading addresses...</span>
                </div>
              ) : addresses.length > 0 ? (
                <div className="space-y-2">
                  {addresses.map((address, index) => (
                    <div key={address.id} className="text-sm">
                      <p className="font-medium">
                        <span className="font-semibold">Address {index + 1}:</span> {formatAddress(address)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  {Array.isArray(coApplicant.addressId) 
                    ? `${coApplicant.addressId.length} address IDs: ${coApplicant.addressId.join(', ')}` 
                    : `Address ID: ${coApplicant.addressId}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
